import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableContainer,
  Chip,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import SearchField from "react-search-field";
import Option from "./Option";

const statusStyles = {
  Creating: { backgroundColor: "#1976d2", label: "InActive" },
  Scheduled: { backgroundColor: "rgb(44 189 186)", label: "InActive" },
  Running: { backgroundColor: "#00a200", label: "Active" },
  Active: { backgroundColor: "#00a200", label: "Active" },
  Stopped: { backgroundColor: "rgb(228 193 3 / 94%)", label: "InActive" },
  Cordoned: { backgroundColor: "rgb(98 95 80 / 94%)", label: "InActive" },
  default: { backgroundColor: "rgb(228 3 3 / 94%)", label: "InActive" },
};

const LabList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(true);
  const [instancelist, setInstanceList] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = useMemo(() => {
    if (!searchTerm) return instancelist;
    return instancelist.filter((val) =>
      val.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [instancelist, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredData.slice(start, start + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const formatDateTime = (date, time, isCloud) => {
    if (!date || !time) return "-";
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dateTime = new Date(`${date}T${time}:00.000Z`).toLocaleString("en-GB", {
      timeZone: timezone,
    });
    const [datePart, timePart] = dateTime.split(",");
    return `${datePart.replace(/\//g, "-")} ${timePart.trim()}`;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/traineroveralllablist/`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        if (response.ok && isMounted) {
          const data = await response.json();
          setInstanceList(data);
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    const intervalId = setInterval(fetchData, 5000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const getDetailPath = (type) => {
    const paths = {
      preconfig: "/app/preconfig/detail",
      custom: "/app/custom/detail",
      hvm: "/app/hypervised/detail",
      sharedvm: "/app/shared/detail",
      cloud: "/app/cloud/detail",
      wise: "/app/wise/detail",
      manual: "/app/manual/detail",
      saas: "/app/saas/detail",
    };
    return paths[type] || "#";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0 }}>
      {/* Search bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 2,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
        }}
      >
        <SearchField
          placeholder="Search by Lab Name"
          onChange={(value) => setSearchTerm(value)}
          classNames="lab-search"
        />
      </Box>

      <Card sx={{ p: 1 }}>
        <TableContainer>
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>Lab Name</TableCell>
                <TableCell>Status</TableCell>
                {!isMobile && <TableCell>Technology</TableCell>}
                <TableCell>Start Duration</TableCell>
                <TableCell>End Duration</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.length > 0 ? (
                paginatedData.map((instance) => (
                  <TableRow hover key={instance.id}>
                    <TableCell>{instance.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          statusStyles[instance.status]?.label ||
                          statusStyles.default.label
                        }
                        style={{
                          backgroundColor:
                            statusStyles[instance.status]?.backgroundColor ||
                            statusStyles.default.backgroundColor,
                          color: "#fff",
                          minWidth: 80,
                        }}
                        size="small"
                      />
                    </TableCell>

                    {!isMobile && <TableCell>{instance.application}</TableCell>}

                    <TableCell>
                      {formatDateTime(
                        instance.start_date,
                        instance.start_time,
                        instance.type === "cloud"
                      )}
                    </TableCell>

                    <TableCell>
                      {formatDateTime(
                        instance.end_date,
                        instance.stop_time,
                        instance.type === "cloud"
                      )}
                    </TableCell>

                    <TableCell>
                      {instance.type !== "cloud" && (
                        <Option
                          InstanceID={instance.id}
                          name={instance.name}
                          type={instance.type}
                          status={instance.status}
                          spent={instance.spent}
                          budget={instance.budget}
                          billingtype={instance.billingtype}
                          endDate={instance.end_date}
                          endTime={instance.stop_time}
                          startDate={instance.start_date}
                          startTime={instance.start_time}
                          depends_on={instance.depends_on}
                          manage_labs={instance.manage_labs}
                          startcron1={instance.startcron1}
                          startcron2={instance.startcron2}
                          repeat_frequency={instance.repeat_frequency}
                          on_days={`${instance.on_days}`}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      <Button
                        size="small"
                        component={Link}
                        to={getDetailPath(instance.type)}
                        onClick={() =>
                          localStorage.setItem("InstanceID", instance.id)
                        }
                      >
                        <ArrowRightAltIcon color="primary" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={isMobile ? 5 : 7} align="center">
                    <Typography variant="body1">No Data Available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredData.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-start", pl: 2 }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default LabList;
